/**
 * Partner list — `/partners` (SPEC screen 1). Server-driven cursor DataTable
 * over `GET /parties` with the contract's four filters (q, kind, role,
 * status). The collection is deliberately PII-minimised: it carries no tax
 * registration, email, phone or address, so nothing on this screen tries to
 * render them.
 *
 * Create is the routed drawer over the list (`/partners/new`), a client-key
 * idempotent POST whose 201 carries the new party's strong ETag.
 */
import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import type { ColumnDef } from "@tanstack/react-table";
import {
  Badge,
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@erp/ui";

import { DataTable, badgeCell, dateTimeCell, textCell } from "@/components/data-table";
import type { SortState } from "@/components/data-table";
import { surfaceProblem } from "@/components/form";
import { ApiError } from "@/lib/api/errors";
import { IDEMPOTENCY_KEY_REUSE_CODE, useIdempotentMutation } from "@/features/shared/ops-api";
import { localizedName } from "@/lib/i18n";

import {
  createParty,
  PARTY_KINDS,
  PARTY_ROLES,
  PARTY_STATUSES,
  type Party,
  type PartyCreate,
  type PartyKind,
  type PartyRole,
  type PartyStatus,
} from "../api";
import { partnersKeys, usePartyList, type PartyListFilters } from "../queries";
import {
  PartyFormDialog,
  type PartyFormErrors,
  type PartyFormValues,
} from "../components/party-form-dialog";

const ALL = "__all__";

export interface PartiesListPageProps {
  onOpenParty: (partyId: string) => void;
  /** Routed create drawer: `/partners/new` opens it, cancel returns to the list. */
  createOpen?: boolean;
  onOpenCreate: () => void;
  onCloseCreate: () => void;
  canCreate?: boolean;
  canOpenDetail?: boolean;
  /** Page size override (tests exercise cursor paging with a small limit). */
  pageLimit?: number;
}

export function PartiesListPage({
  onOpenParty,
  createOpen = false,
  onOpenCreate,
  onCloseCreate,
  canCreate = true,
  canOpenDetail = true,
  pageLimit,
}: PartiesListPageProps) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();

  const [q, setQ] = useState("");
  const [kind, setKind] = useState<PartyKind | typeof ALL>(ALL);
  const [role, setRole] = useState<PartyRole | typeof ALL>(ALL);
  const [status, setStatus] = useState<PartyStatus | typeof ALL>(ALL);
  const [sort, setSort] = useState<SortState>(null);
  const [createErrors, setCreateErrors] = useState<PartyFormErrors>({});

  const filters = useMemo<PartyListFilters>(() => {
    const built: PartyListFilters = {};
    if (q.trim() !== "") built.q = q.trim();
    if (kind !== ALL) built.kind = kind;
    if (role !== ALL) built.role = role;
    if (status !== ALL) built.status = status;
    return built;
  }, [q, kind, role, status]);

  const hasFilters = Object.keys(filters).length > 0;
  const query = usePartyList(filters, sort, pageLimit);

  const createMutation = useIdempotentMutation<{ id: string }, PartyCreate>({
    mutationFn: async (input, key) => (await createParty(input, key)).data,
    onSuccess: (party) => {
      setCreateErrors({});
      onCloseCreate();
      void queryClient.invalidateQueries({ queryKey: partnersKeys.parties.all });
      toast({ variant: "success", title: t("partners.create.created") });
      onOpenParty(party.id);
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === "tax_registration_invalid") {
        setCreateErrors({
          taxNumber: error.problem?.detail ?? t("partners.create.taxIncomplete"),
        });
        return;
      }
      if (error instanceof ApiError && error.code === IDEMPOTENCY_KEY_REUSE_CODE) {
        surfaceProblem(error, t);
        void queryClient.invalidateQueries({ queryKey: partnersKeys.parties.all });
        return;
      }
      surfaceProblem(error, t);
    },
  });

  const submitCreate = (values: PartyFormValues): void => {
    setCreateErrors({});
    const input: PartyCreate = {
      kind: values.kind,
      display_name: values.displayName,
      default_currency: values.defaultCurrency,
      ...(values.taxCountry !== "" && values.taxNumber !== ""
        ? {
            tax_registration: {
              country_code: values.taxCountry,
              registration_no: values.taxNumber,
            },
          }
        : {}),
    };
    createMutation.mutate(input);
  };

  const columns = useMemo<ColumnDef<Party>[]>(
    () => [
      {
        accessorKey: "display_name",
        id: "display_name",
        header: t("partners.list.columns.name"),
        // A partner may carry an Arabic name; an Arabic reader sees it, and a
        // partner without one keeps its Latin name rather than going blank.
        cell: (ctx) => (
          <span dir="auto" className="font-medium">
            {localizedName(
              ctx.row.original.display_name,
              ctx.row.original.display_name_ar,
              i18n.language,
            )}
          </span>
        ),
      },
      {
        accessorKey: "kind",
        id: "kind",
        header: t("partners.list.columns.kind"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<PartyKind>();
          return badgeCell(
            value,
            { person: "outline", org: "secondary" },
            t(`partners.kind.${value}`),
          );
        },
      },
      {
        accessorKey: "roles",
        id: "roles",
        header: t("partners.list.columns.roles"),
        enableSorting: false,
        cell: (ctx) => {
          const roles = ctx.getValue<readonly PartyRole[]>() ?? [];
          if (roles.length === 0) {
            return <span className="text-muted-foreground">{t("partners.list.noRoles")}</span>;
          }
          return (
            <span className="flex flex-wrap gap-1">
              {roles.map((value) => (
                <Badge key={value} variant={value === "customer" ? "default" : "secondary"}>
                  {t(`partners.role.${value}`)}
                </Badge>
              ))}
            </span>
          );
        },
      },
      {
        accessorKey: "default_currency",
        id: "default_currency",
        header: t("partners.list.columns.currency"),
        enableSorting: false,
        cell: (ctx) => (
          <span dir="ltr" className="font-mono text-xs">
            {textCell(ctx.getValue<string | undefined>())}
          </span>
        ),
      },
      {
        accessorKey: "status",
        id: "status",
        header: t("partners.list.columns.status"),
        enableSorting: false,
        cell: (ctx) => {
          const value = ctx.getValue<PartyStatus>();
          return badgeCell(
            value,
            { active: "success", archived: "secondary", pseudonymized: "outline" },
            t(`partners.status.${value}`),
          );
        },
      },
      {
        accessorKey: "updated_at",
        id: "updated_at",
        header: t("partners.list.columns.updated"),
        cell: (ctx) => dateTimeCell(ctx.getValue<string>()),
      },
    ],
    [t, i18n.language],
  );

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold">{t("partners.list.title")}</h1>
          <p className="text-sm text-muted-foreground">{t("partners.list.subtitle")}</p>
        </div>
        {canCreate ? <Button onClick={onOpenCreate}>{t("partners.list.newParty")}</Button> : null}
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="partners-filter-q">{t("partners.list.filters.search")}</Label>
          <Input
            id="partners-filter-q"
            dir="auto"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            placeholder={t("partners.list.filters.searchPlaceholder")}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="partners-filter-kind">{t("partners.list.filters.kind")}</Label>
          <Select value={kind} onValueChange={(value) => setKind(value as PartyKind | typeof ALL)}>
            <SelectTrigger id="partners-filter-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("partners.common.all")}</SelectItem>
              {PARTY_KINDS.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`partners.kind.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="partners-filter-role">{t("partners.list.filters.role")}</Label>
          <Select value={role} onValueChange={(value) => setRole(value as PartyRole | typeof ALL)}>
            <SelectTrigger id="partners-filter-role">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("partners.common.all")}</SelectItem>
              {PARTY_ROLES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`partners.role.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="partners-filter-status">{t("partners.list.filters.status")}</Label>
          <Select
            value={status}
            onValueChange={(value) => setStatus(value as PartyStatus | typeof ALL)}
          >
            <SelectTrigger id="partners-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>{t("partners.common.all")}</SelectItem>
              {PARTY_STATUSES.map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`partners.status.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {hasFilters ? (
        <div>
          <Button
            type="button"
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs"
            onClick={() => {
              setQ("");
              setKind(ALL);
              setRole(ALL);
              setStatus(ALL);
            }}
          >
            {t("partners.list.clearFilters")}
          </Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        query={query}
        getRowId={(row) => row.id}
        sort={sort}
        onSortChange={setSort}
        {...(canOpenDetail ? { onRowClick: (row: Party) => onOpenParty(row.id) } : {})}
        storageKey="partners.parties.list.columns"
        emptyMessage={hasFilters ? t("partners.list.emptyFiltered") : t("partners.list.empty")}
        errorMessage={t("partners.list.loadError")}
      />

      {canCreate ? (
        <PartyFormDialog
          open={createOpen}
          mode="create"
          pending={createMutation.isPending}
          serverErrors={createErrors}
          onSubmit={submitCreate}
          onOpenChange={(open) => {
            if (!open) {
              setCreateErrors({});
              onCloseCreate();
            }
          }}
        />
      ) : null}
    </div>
  );
}
